import {
    Paper,
    Box,
    TextField,
    Button,
    CircularProgress,
    Typography,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ContentContainer } from '../../components/ContentContainer';
import { GenericToolbar } from '../../components/GenericToolbar';
import { useSelectedOrganizationData, isRecordFound } from '../../hooks/data-hooks';
import { isDataLoaded } from '../../types';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { pushAppRoute } from '../../data/domain/route-manager';

import { useDeviceMaster } from '../../data/DataProviders/DeviceMasterProvider';
import { DataIndicators } from '../../components/DataIndicators';
import { useDispatch, useSelector } from 'react-redux';
import { SierraDeviceSelectors } from '../../selectors/device-selectors';
import { ChildDeviceRegistrationData, SierraDeviceData } from '../../types/device';
import { Thunks } from '../../actions';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { useAuthHelper } from '../../components/AuthContextProvider';
import deviceStyles from './DeviceStyle';
import { useSierraGatewayDeviceDetails } from '../../data/DataProviders/SierraGatewayDeviceDetailsProvider';
import {
    getDeviceInfoFromMasterData,
    getExistingChildDevicesUnitId,
    IPAddressValidation,
    getChildDeviceLimit,
} from '../../lib/device-helpers';
import {
    DECIMAL_NUMBER,
    FX30S_SERIAL,
    FX30_ETHERNET,
    ONLY_NUMBER,
    RESTRICT_SPEC_REGEX,
    RESTRICT_DEVICE_WITHOUT_SPACE_REGEX,
} from '../../constants';
import { uniq } from 'lodash';
import { ConfirmationPopUp } from '../../components/ConfirmationPopUp/ConfirmationPopUp';
import { SierraDeviceActions } from '../../actions/actions/sierra-device-actions';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { HeaderInfo } from '../../components/HeaderInfo/HeaderInfo';
import { useDeviceDetails } from '../../data/DataProviders/DeviceDetailsProvider';
import { NavigationPromptModal } from '../../components/NavigationPromptModal/NavigationPromptModal';
import { useHistory } from 'react-router-dom';

export const AddChildDevice: React.FC = () => {
    const deviceDetails = useDeviceDetails();
    const { t } = useLanguageLocale();
    const history = useHistory();
    const dispatch = useDispatch();
    const classes = deviceStyles();
    const { addToast } = useToasts();
    const authHelper = useAuthHelper();
    const currentAppRoute = useCurrentAppRoute();
    const selectedOrganization = useSelectedOrganizationData();
    const gatewayDeviceDetails = useSierraGatewayDeviceDetails();
    const deviceMasterData = useDeviceMaster();
    const exerthermUnitIdValidation = useSelector(SierraDeviceSelectors.exerthermUnitIdValidation);
    const orgName = isRecordFound(selectedOrganization) ? selectedOrganization.value.name : '';
    const [deviceTypeList, setDeviceTypeList] = useState<any[]>([]);
    const [deviceTypeValue, setDeviceTypeValue] = React.useState('');
    const [childDevice, setChildDevice] = useState<ChildDeviceRegistrationData>();
    const childDeviceState = useSelector(SierraDeviceSelectors.deviceRegistration);
    const [ipAddressError, setIPAddressError] = React.useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const gatewayDeviceName = isDataLoaded(gatewayDeviceDetails) ? gatewayDeviceDetails.value.name : '';
    const [unmodifiedChildDevice, setUnmodifiedChildDevice] = useState<ChildDeviceRegistrationData | null>(null);

    useEffect(() => {
        const isModified = JSON.stringify(unmodifiedChildDevice) !== JSON.stringify(childDevice);
        setIsDirty(isModified);
    }, [childDevice, unmodifiedChildDevice]);

    const navigateToDeviceProfile = (): void => {
        if (currentAppRoute.type === 'AddChildDevice')
            pushAppRoute(history, {
                type: 'DeviceProfile',
                orgId: currentAppRoute.orgId,
                locationId: currentAppRoute.locationId,
                siteId: currentAppRoute.siteId,
                deviceId: currentAppRoute.deviceId,
            });
    };
    const handleClose = (): void => {
        navigateToDeviceProfile();
    };
    useEffect(() => {
        if (childDevice) setUnmodifiedChildDevice(childDevice);
        dispatch(SierraDeviceActions.sierraChildDeviceRegistrationUnmounted());
    }, []); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (exerthermUnitIdValidation) {
            addToast(
                'You have already configured maximum Device to use this unit id for the Connected Device. Please update this field to an unused unit id.',
                'error',
                'Maximum unit id limit exhausted'
            );
            dispatch(SierraDeviceActions.exerthermUnitIdValidation(false));
        }
    }, [exerthermUnitIdValidation]); //eslint-disable-line react-hooks/exhaustive-deps

    const isDisableEthernet =
        childDevice &&
        deviceTypeValue !== '0' &&
        childDevice.name !== '' &&
        childDevice.ipAddress !== '' &&
        childDevice.unitId !== 0 &&
        childDevice.unitId.toString() !== '' &&
        childDevice.ipPort > 0;

    const isDisableFX30Serial =
        childDevice &&
        deviceTypeValue !== '0' &&
        childDevice.name !== '' &&
        childDevice.unitId !== 0 &&
        childDevice.unitId.toString() !== '';

    const isDisable = (): boolean => {
        if (!childDeviceState.loading && isDataLoaded(gatewayDeviceDetails))
            return gatewayDeviceDetails.value.model.toLowerCase() === FX30_ETHERNET
                ? !isDisableEthernet
                : gatewayDeviceDetails.value.model.toLowerCase() === FX30S_SERIAL
                ? !isDisableFX30Serial
                : true;
        return true;
    };

    useEffect(() => {
        if (isDataLoaded(gatewayDeviceDetails)) {
            deviceMasterData.getDevicesInfo(orgName, gatewayDeviceDetails.value.childDeviceTypeList);
        }
    }, [gatewayDeviceDetails]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (
            isDataLoaded(deviceMasterData.deviceInfo) &&
            deviceMasterData.deviceInfo.value &&
            deviceMasterData.deviceInfo.value.length > 0 &&
            isDataLoaded(gatewayDeviceDetails) &&
            deviceMasterData.rowKey === gatewayDeviceDetails.value.childDeviceTypeList
        ) {
            const childDeviceInfo = getDeviceInfoFromMasterData(deviceMasterData.deviceInfo.value);
            if (childDeviceInfo) {
                setDeviceTypeList(childDeviceInfo.childDeviceTypes);
            }
        }
    }, [deviceMasterData]); //eslint-disable-line react-hooks/exhaustive-deps

    const getMaxUnitId = (gatewayDetails: SierraDeviceData): number => {
        const unitIdchildList = getExistingChildDevicesUnitId(gatewayDetails.configTreeJson || '');
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const maxValue = unitIdchildList.length > 0 ? Math.max.apply(0, unitIdchildList) + 1 : 1;
        if (gatewayDetails.model.toLowerCase() === FX30S_SERIAL && maxValue > 247) return 247;
        if (gatewayDetails.model.toLowerCase() === FX30_ETHERNET && maxValue > 247 && maxValue !== 255) return 255;
        return maxValue;
    };
    useEffect(() => {
        if (isDataLoaded(gatewayDeviceDetails) && currentAppRoute.type === 'AddChildDevice') {
            const device: ChildDeviceRegistrationData = {
                siteId: currentAppRoute.siteId,
                name: '',
                modelType: '',
                deviceProfileId: '0',
                gatewayDeviceId: gatewayDeviceDetails.value.pxWhiteGatewayId,
                validateInput: true,
                ipAddress: '',
                parentModelType: gatewayDeviceDetails.value.model,
                unitId: getMaxUnitId(gatewayDeviceDetails.value),
                ipPort: 502,
            };
            setChildDevice(device);
        }
    }, [gatewayDeviceDetails]); //eslint-disable-line react-hooks/exhaustive-deps

    const onChangeValues = (value: string, type: string): void => {
        if (childDevice) {
            const deviceNameObj: any = { ...childDevice };
            deviceNameObj[`${type}`] = value;
            setChildDevice(deviceNameObj);
        }
    };
    const handleDeviceType = (e: SelectChangeEvent): void => {
        if (childDevice) {
            const selectedObj = deviceTypeList.find((x) => x.value.modelType === e.target.value);
            setDeviceTypeValue(e.target.value);
            const deviceModelObj: ChildDeviceRegistrationData = { ...childDevice };
            deviceModelObj.deviceProfileId = selectedObj.value.profileId;
            deviceModelObj.modelType = e.target.value;
            setChildDevice(deviceModelObj);
        }
    };

    const onChangeDeviceName = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (RESTRICT_DEVICE_WITHOUT_SPACE_REGEX.test(e.target.value) && childDevice) {
            onChangeValues(e.target.value, 'name');
        }
    };
    const onChangeAddress = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (childDevice && (e.target.value === '' || DECIMAL_NUMBER.test(e.target.value))) {
            setIPAddressError(false);
            onChangeValues(e.target.value, 'ipAddress');
        }
    };
    const onChangeIpPort = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (RESTRICT_SPEC_REGEX.test(e.target.value) && childDevice) {
            onChangeValues(e.target.value, 'ipPort');
        }
    };

    const onChangeUnitId = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (
            (e.target.value === '' && childDevice) ||
            (ONLY_NUMBER.test(e.target.value) &&
                childDevice &&
                childDevice.parentModelType &&
                ((childDevice.parentModelType.toLowerCase() === FX30S_SERIAL && Number(e.target.value) <= 247) ||
                    (childDevice.parentModelType.toLowerCase() === FX30_ETHERNET &&
                        (Number(e.target.value) <= 247 || Number(e.target.value) === 255))))
        ) {
            onChangeValues(e.target.value, 'unitId');
        }
    };

    const validationForNoDevices = (): boolean | undefined => {
        if (childDevice && isDataLoaded(deviceDetails)) {
            const selectedObj = deviceTypeList.find((x) => x.value.modelType === deviceTypeValue);
            const existingChildDeviceModel: string[] = deviceDetails.value.devices.map((x: any) => x.profileId);
            const similiarProfileDevice = existingChildDeviceModel.filter((x) => x === selectedObj.value.profileId);
            const uniqueDeviceType = uniq(existingChildDeviceModel);
            const childDeviceLimit = getChildDeviceLimit(selectedObj);
            if (childDeviceLimit) {
                if (
                    uniqueDeviceType.some((device: string) => device === selectedObj.value.profileId) &&
                    similiarProfileDevice.length >= childDeviceLimit.allowedCreation
                ) {
                    addToast(
                        `Cannot add more than ${childDeviceLimit.allowedCreation} ${selectedObj.value.modelType} device.`,
                        'error'
                    );
                    return false;
                }
            }
            return true;
        }
    };
    const callChildRegistrationWithUnitId = (allowDuplicateUnitId: number): void => {
        if (childDevice && isDataLoaded(gatewayDeviceDetails)) {
            dispatch(
                Thunks.SierraDevice.ChildDeviceRegistrationWithUnitIdValidation(
                    gatewayDeviceDetails.value.pxWhiteGatewayId,
                    gatewayDeviceDetails.value.siteId,
                    allowDuplicateUnitId,
                    childDevice,
                    authHelper,
                    addToast
                )
            );
        }
    };

    const onSubmit = (): void => {
        let allowDuplicateUnitId = 0;
        if (childDevice && isDataLoaded(gatewayDeviceDetails) && isDataLoaded(deviceDetails)) {
            const IPValid = IPAddressValidation(childDevice.parentModelType, childDevice.ipAddress);
            setIPAddressError(!IPValid);
            const childDeviceList = deviceDetails.value.devices;

            if (IPValid) {
                if (childDeviceList && childDeviceList.length > 0) {
                    dispatch(SierraDeviceActions.exerthermUnitIdValidation(false));
                    const validation = validationForNoDevices();
                    if (validation) {
                        const selectedObj = deviceTypeList.find((x) => x.value.modelType === deviceTypeValue);
                        const childDeviceLimit = getChildDeviceLimit(selectedObj);
                        if (childDeviceLimit && childDeviceLimit.allowedUnitIdDulipcate > 0) {
                            allowDuplicateUnitId = childDeviceLimit.allowedUnitIdDulipcate;
                        }
                        callChildRegistrationWithUnitId(allowDuplicateUnitId);
                    }
                } else {
                    dispatch(Thunks.SierraDevice.ChildDeviceRegistration(childDevice, authHelper, addToast));
                }
            }
            // else if (gatewayDeviceDetails.value.model.toLowerCase() === FX30S_SERIAL) {
            //     dispatch(Thunks.SierraDevice.ChildDeviceRegistration(childDevice, authHelper, addToast));
            // }
        }
    };

    return (
        <>
            <GenericToolbar
                showBackButton
                backButtonOnClick={(): void => {
                    if (currentAppRoute.type === 'AddChildDevice')
                        pushAppRoute(history, {
                            type: 'DeviceProfile',
                            orgId: currentAppRoute.orgId,
                            locationId: currentAppRoute.locationId,
                            siteId: currentAppRoute.siteId,
                            deviceId: currentAppRoute.deviceId,
                        });
                }}
                title="Add Device"
                subtitle={gatewayDeviceName}
            />

            <ContentContainer>
                <Paper style={{ padding: 56 }}>
                    {!isDataLoaded(gatewayDeviceDetails) && (
                        <DataIndicators
                            errorMessage="Gateway record not found"
                            emptyMessage="Gateway record not found"
                            inline
                            data={gatewayDeviceDetails}
                            emptyIcon="Devices"
                            isEmpty={false}
                            isDetailedInfo={false}
                        />
                    )}

                    {isDataLoaded(gatewayDeviceDetails) && childDevice && (
                        <>
                            <Box>
                                {!isDataLoaded(deviceMasterData.deviceInfo) && (
                                    <DataIndicators
                                        errorMessage="Record not found"
                                        emptyMessage="Record not found"
                                        inline
                                        data={deviceMasterData.deviceInfo}
                                        emptyIcon="Devices"
                                        isEmpty={false}
                                        isDetailedInfo={false}
                                    />
                                )}
                                {isDataLoaded(deviceMasterData.deviceInfo) && (
                                    <>
                                        <HeaderInfo title="Device Information" entity="to add a Device" />
                                        <Box className={classes.padding8}>
                                            <FormControl variant="filled" required className={`${classes.fullWidth}`}>
                                                <InputLabel id="deviceTypeData" data-testid="device-label-dd">
                                                    Supported Device Type
                                                </InputLabel>
                                                <Select
                                                    label="deviceType"
                                                    onChange={(e): void => {
                                                        handleDeviceType(e);
                                                    }}
                                                    variant="filled"
                                                    defaultValue="0"
                                                    value={deviceTypeValue}
                                                    required
                                                    inputProps={{ 'data-testid': 'device-dd' }}
                                                >
                                                    {isDataLoaded(deviceMasterData.deviceInfo) &&
                                                        deviceTypeList.map(
                                                            (
                                                                deviceData: {
                                                                    key: string;
                                                                    value: {
                                                                        profileId: string;
                                                                        displayName: string;
                                                                        modelType: string;
                                                                    };
                                                                },
                                                                index: number
                                                            ) => (
                                                                <MenuItem
                                                                    key={index}
                                                                    value={deviceData.value.modelType}
                                                                    data-testid={`device-type-menu${index}`}
                                                                >
                                                                    {deviceData.value.displayName}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        <Box className={classes.padding8}>
                                            <TextField
                                                inputProps={{ 'data-testid': 'device-name' }}
                                                fullWidth
                                                required
                                                variant="filled"
                                                id="DeviceName"
                                                label="Device Name"
                                                value={childDevice.name}
                                                onChange={(e): void => {
                                                    onChangeDeviceName(e);
                                                }}
                                            />
                                        </Box>
                                        <Box className={classes.padding8}>
                                            {gatewayDeviceDetails.value.model.toLowerCase() === FX30_ETHERNET && (
                                                <TextField
                                                    inputProps={{ 'data-testid': 'ip-address' }}
                                                    required
                                                    fullWidth
                                                    id="ModbusAddress"
                                                    label={'Modbus (TCP/IP) Address'}
                                                    error={ipAddressError}
                                                    helperText={ipAddressError ? 'Invalid Modbus (TCP/IP) Address' : ''}
                                                    variant="filled"
                                                    value={childDevice.ipAddress}
                                                    onChange={(e): void => {
                                                        onChangeAddress(e);
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Box className={classes.padding8}>
                                            <TextField
                                                type="number"
                                                inputProps={{ 'data-testid': 'unit-id', maxLength: 3 }}
                                                required
                                                fullWidth
                                                helperText={
                                                    gatewayDeviceDetails.value.model.toLowerCase() === FX30_ETHERNET
                                                        ? 'Valid client Unit ID are 1-247 and 255.'
                                                        : 'Valid client Modbus RTU Addresses are 1-247 .'
                                                }
                                                id="unit-id"
                                                label={
                                                    gatewayDeviceDetails.value.model.toLowerCase() === FX30_ETHERNET
                                                        ? 'Unit ID'
                                                        : 'Modbus (RTU) Address'
                                                }
                                                variant="filled"
                                                value={childDevice.unitId === 0 ? '' : childDevice.unitId}
                                                onChange={(e): void => {
                                                    onChangeUnitId(e);
                                                }}
                                            />
                                        </Box>
                                        {gatewayDeviceDetails.value.model.toLowerCase() === FX30_ETHERNET && (
                                            <Box className={classes.padding8}>
                                                <TextField
                                                    inputProps={{ 'data-testid': 'ip-port', maxLength: 5 }}
                                                    fullWidth
                                                    required
                                                    variant="filled"
                                                    id="ipport"
                                                    label="IP Port"
                                                    value={childDevice.ipPort}
                                                    onChange={(e): void => {
                                                        onChangeIpPort(e);
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Box className={classes.rightSideButtonPosition}>
                                            <Button
                                                style={{ marginRight: 16 }}
                                                variant="outlined"
                                                color="primary"
                                                data-testid="cancel-device"
                                                onClick={(): void => navigateToDeviceProfile()}
                                            >
                                                {t('ACTIONS.CANCEL')}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                disabled={isDisable()}
                                                startIcon={
                                                    childDeviceState.loading ? (
                                                        <CircularProgress size="1rem" style={{ color: 'white' }} />
                                                    ) : (
                                                        <></>
                                                    )
                                                }
                                                color="primary"
                                                data-testid="add-device-button"
                                                onClick={onSubmit}
                                            >
                                                Submit
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </>
                    )}
                    {!childDeviceState.loading && childDeviceState.success && (
                        <ConfirmationPopUp
                            title="Submission Successful!"
                            titleColor="primary"
                            body={
                                <>
                                    <Typography variant="subtitle1" style={{ marginBottom: 20 }}>
                                        Your device is being remotely configured.
                                    </Typography>
                                </>
                            }
                            okButtonText={'OK'}
                            show={childDeviceState.success}
                            handleOk={handleClose}
                            okButtonShow={true}
                            width="xs"
                        ></ConfirmationPopUp>
                    )}
                </Paper>
                {!childDeviceState.success && <NavigationPromptModal isDirty={isDirty} />}
            </ContentContainer>
        </>
    );
};
